// client/src/pages/Admin/ProjectManager/utils/projectHelpers.ts
/**
 * PROJECT HELPERS
 * 
 * This file contains helper functions for formatting, transforming,
 * and manipulating project data.
 * 
 * @module ProjectManager/Utils/Helpers
 */

import type { LucideIcon } from 'lucide-react';
import { Pin, TrendingUp } from 'lucide-react';
import {
    Project,
    ProjectStatus,
    ProjectCategory,
    ProjectImpact,
    ProjectStatusLabels,
    ProjectCategoryLabels,
    ProjectImpactLabels,
    ProjectImpactColors
} from '../types/project.types';
import {
    STATUS_CONFIG,
    IMPACT_CONFIG,
    CATEGORY_CONFIG
} from '../constants/project.constants';

// ============================================
// STATUS HELPERS
// ============================================

/**
 * Get human-readable status label
 * @param status - Project status
 * @returns Formatted status label
 * 
 * @example
 * formatStatusLabel('draft') // 'Draft'
 * formatStatusLabel('coming_soon') // 'Coming Soon'
 */
export const formatStatusLabel = (status: ProjectStatus): string => {
    if (!status) return '';
    return ProjectStatusLabels[status] || status;
};

/**
 * Get status color class for UI styling
 * @param status - Project status
 * @returns CSS color class
 * 
 * @example
 * getStatusColor('published') // 'status-published'
 */
export const getStatusColor = (status: ProjectStatus): string => {
    if (!status) return 'status-default';
    const config = STATUS_CONFIG[status];
    return config ? `status-${status}` : 'status-default';
};

/**
 * Get status icon (emoji)
 * @param status - Project status
 * @returns Status icon emoji
 * 
 * @example
 * getStatusIcon('published') // '✅'
 */
export const getStatusIcon = (status: ProjectStatus): LucideIcon => {
    if (!status) return Pin;
    const config = STATUS_CONFIG[status];
    return config ? config.icon : Pin;
};

/**
 * Get status description for tooltips/help text
 * @param status - Project status
 * @returns Status description
 * 
 * @example
 * getStatusDescription('draft') // 'Project is in draft mode...'
 */
export const getStatusDescription = (status: ProjectStatus): string => {
    if (!status) return '';
    const config = STATUS_CONFIG[status];
    return config ? config.description : '';
};

/**
 * Check if a status is active/visible to the public
 * @param status - Project status
 * @returns True if the project is public
 * 
 * @example
 * isPublicStatus('published') // true
 * isPublicStatus('draft') // false
 */
export const isPublicStatus = (status: ProjectStatus): boolean => {
    return status === 'published';
};

/**
 * Check if a status is editable
 * @param status - Project status
 * @returns True if the project can be edited
 * 
 * @example
 * isEditableStatus('draft') // true
 * isEditableStatus('archived') // false
 */
export const isEditableStatus = (status: ProjectStatus): boolean => {
    return status !== 'archived';
};

// ============================================
// IMPACT HELPERS
// ============================================

/**
 * Get human-readable impact label
 * @param impact - Project impact level
 * @returns Formatted impact label
 * 
 * @example
 * formatImpactLabel('high') // 'High Impact'
 */
export const formatImpactLabel = (impact: ProjectImpact): string => {
    if (!impact) return '';
    return ProjectImpactLabels[impact] || impact;
};

/**
 * Get impact color class for UI styling
 * @param impact - Project impact level
 * @returns CSS color class
 * 
 * @example
 * getImpactColor('high') // 'impact-high'
 */
export const getImpactColor = (impact: ProjectImpact): string => {
    if (!impact) return 'impact-default';
    return ProjectImpactColors[impact] || 'impact-default';
};

/**
 * Get impact description for tooltips/help text
 * @param impact - Project impact level
 * @returns Impact description
 * 
 * @example
 * getImpactDescription('critical') // 'Project has critical impact...'
 */
export const getImpactDescription = (impact: ProjectImpact): string => {
    if (!impact) return '';
    const config = IMPACT_CONFIG[impact];
    return config ? config.description : '';
};

/**
 * Get impact icon (emoji)
 * @param impact - Project impact level
 * @returns Impact icon emoji
 * 
 * @example
 * getImpactIcon('critical') // '⚠️'
 */
export const getImpactIcon = (impact: ProjectImpact): LucideIcon => {
    if (!impact) return TrendingUp;
    const config = IMPACT_CONFIG[impact];
    return config ? config.icon : TrendingUp;
};

// ============================================
// CATEGORY HELPERS
// ============================================

/**
 * Get human-readable category label
 * @param category - Project category
 * @returns Formatted category label
 * 
 * @example
 * formatCategoryLabel('education') // 'Education'
 */
export const formatCategoryLabel = (category: ProjectCategory): string => {
    if (!category) return 'Uncategorized';
    return ProjectCategoryLabels[category] || category;
};

/**
 * Get category emoji
 * @param category - Project category
 * @returns Category emoji
 * 
 * @example
 * getCategoryEmoji('health') // '🏥'
 */
export const getCategoryEmoji = (category: ProjectCategory): LucideIcon => {
    if (!category) return Pin;
    const config = CATEGORY_CONFIG[category];
    return config ? config.icon : Pin;
};

/**
 * Get category color
 * @param category - Project category
 * @returns Hex color code
 * 
 * @example
 * getCategoryColor('governance') // '#8b5cf6'
 */
export const getCategoryColor = (category: ProjectCategory): string => {
    if (!category) return '#6b7280';
    const config = CATEGORY_CONFIG[category];
    return config ? config.color : '#6b7280';
};

/**
 * Get category description
 * @param category - Project category
 * @returns Category description
 * 
 * @example
 * getCategoryDescription('education') // 'Educational programs and initiatives'
 */
export const getCategoryDescription = (category: ProjectCategory): string => {
    if (!category) return '';
    const config = CATEGORY_CONFIG[category];
    return config ? config.description : '';
};

// ============================================
// DATE HELPERS
// ============================================

/**
 * Format a date for display
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 * 
 * @example
 * formatDate(new Date()) // 'Jan 1, 2024'
 * formatDate(new Date(), { weekday: 'long' }) // 'Monday'
 */
export const formatDate = (
    date: Date | string | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }
): string => {
    if (!date) return '';
    try {
        const d = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, options);
    } catch {
        return '';
    }
};

/**
 * Format a date relative to now
 * @param date - Date to format
 * @returns Relative time string
 * 
 * @example
 * formatRelativeDate(new Date()) // 'Today'
 * formatRelativeDate(2 days ago) // '2 days ago'
 */
export const formatRelativeDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    try {
        const d = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(d.getTime())) return '';

        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
        return formatDate(d);
    } catch {
        return '';
    }
};

/**
 * Check if a project is ongoing
 * @param project - Project object
 * @returns True if the project is ongoing
 */
export const isProjectOngoing = (project: Project): boolean => {
    if (!project) return false;
    if (project.isOngoing) return true;
    if (project.endDate) {
        const endDate = typeof project.endDate === 'string' 
            ? new Date(project.endDate) 
            : project.endDate;
        return endDate > new Date();
    }
    return true;
};

/**
 * Get project duration in days
 * @param project - Project object
 * @returns Number of days or null if invalid
 */
export const getProjectDuration = (project: Project): number | null => {
    if (!project || !project.startDate) return null;
    try {
        const start = typeof project.startDate === 'string' 
            ? new Date(project.startDate) 
            : project.startDate;
        const end = project.endDate 
            ? (typeof project.endDate === 'string' ? new Date(project.endDate) : project.endDate)
            : new Date();
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
};

// ============================================
// TEXT HELPERS
// ============================================

/**
 * Truncate text to a specified length
 * @param text - Text to truncate
 * @param length - Maximum length
 * @param suffix - Suffix to append (default: '...')
 * @returns Truncated text
 * 
 * @example
 * truncateText('Long text here', 10) // 'Long text ...'
 */
export const truncateText = (
    text: string,
    length: number = 150,
    suffix: string = '...'
): string => {
    if (!text) return '';
    const cleanText = text.trim();
    if (cleanText.length <= length) return cleanText;
    return cleanText.substring(0, length).trim() + suffix;
};

/**
 * Strip HTML tags from text
 * @param html - HTML string
 * @returns Plain text
 * 
 * @example
 * stripHtml('<p>Hello</p>') // 'Hello'
 */
export const stripHtml = (html: string): string => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

/**
 * Get word count of text
 * @param text - Text to count words in
 * @returns Number of words
 */
export const getWordCount = (text: string): number => {
    if (!text) return 0;
    const cleanText = text.trim();
    if (!cleanText) return 0;
    return cleanText.split(/\s+/).length;
};

/**
 * Generate a slug from text
 * @param text - Text to slugify
 * @returns URL-friendly slug
 * 
 * @example
 * generateSlug('My Project Name') // 'my-project-name'
 */
export const generateSlug = (text: string): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
};

// ============================================
// PROJECT HELPERS
// ============================================

/**
 * Get project status counts
 * @param projects - Array of projects
 * @returns Counts by status
 */
export const getProjectStatusCounts = (projects: Project[]) => {
    if (!Array.isArray(projects)) {
        return { all: 0, draft: 0, published: 0, archived: 0, coming_soon: 0 };
    }
    return {
        all: projects.length,
        draft: projects.filter(p => p.status === 'draft').length,
        published: projects.filter(p => p.status === 'published').length,
        archived: projects.filter(p => p.status === 'archived').length,
        coming_soon: projects.filter(p => p.status === 'coming_soon').length
    };
};

/**
 * Check if a project matches a search term
 * @param project - Project to check
 * @param searchTerm - Search term
 * @returns True if the project matches the search
 */
export const projectMatchesSearch = (
    project: Project,
    searchTerm: string
): boolean => {
    if (!searchTerm) return true;
    if (!project) return false;

    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;

    const searchableFields = [
        project.title,
        project.subtitle,
        project.description,
        project.fullDescription,
        ...(project.tags || []),
        ...(project.regions || []),
        ...(project.districts || [])
    ];

    return searchableFields.some(field =>
        field && field.toLowerCase().includes(search)
    );
};

/**
 * Sort projects by various criteria
 * @param projects - Array of projects
 * @param sortBy - Sort criterion
 * @param direction - Sort direction
 * @returns Sorted array of projects
 */
export const sortProjects = (
    projects: Project[],
    sortBy: string,
    direction: 'asc' | 'desc' = 'desc'
): Project[] => {
    if (!Array.isArray(projects)) return [];
    
    const sorted = [...projects];
    const dir = direction === 'desc' ? -1 : 1;

    switch (sortBy) {
        case 'newest':
            sorted.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return (dateB - dateA) * dir;
            });
            break;

        case 'oldest':
            sorted.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return (dateA - dateB) * dir;
            });
            break;

        case 'alphabetical':
        case 'alphabetical-desc':
            sorted.sort((a, b) => {
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB) * dir;
            });
            break;

        case 'recently-updated':
            sorted.sort((a, b) => {
                const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return (dateB - dateA) * dir;
            });
            break;

        case 'start-date':
            sorted.sort((a, b) => {
                const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
                const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
                return (dateB - dateA) * dir;
            });
            break;

        case 'views':
            sorted.sort((a, b) => {
                const viewsA = a.views || 0;
                const viewsB = b.views || 0;
                return (viewsB - viewsA) * dir;
            });
            break;

        case 'impact': {
            const impactOrder = { critical: 4, high: 3, medium: 2, low: 1 };
            sorted.sort((a, b) => {
                const impactA = impactOrder[a.impact || 'low'] || 0;
                const impactB = impactOrder[b.impact || 'low'] || 0;
                return (impactB - impactA) * dir;
            });
            break;
        }

        default:
            break;
    }

    return sorted;
};

/**
 * Filter projects by various criteria
 * @param projects - Array of projects
 * @param filters - Filter options
 * @returns Filtered array of projects
 */
export const filterProjects = (
    projects: Project[],
    filters: {
        status?: ProjectStatus | 'all';
        category?: ProjectCategory | 'all';
        impact?: ProjectImpact | 'all';
        searchTerm?: string;
        featured?: boolean | 'all';
        isOngoing?: boolean | 'all';
    }
): Project[] => {
    if (!Array.isArray(projects)) return [];

    let filtered = [...projects];

    if (filters.status && filters.status !== 'all') {
        filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.category && filters.category !== 'all') {
        filtered = filtered.filter(p => p.category === filters.category);
    }

    if (filters.impact && filters.impact !== 'all') {
        filtered = filtered.filter(p => p.impact === filters.impact);
    }

    if (filters.featured && filters.featured !== 'all') {
        filtered = filtered.filter(p => p.featured === filters.featured);
    }

    if (filters.isOngoing && filters.isOngoing !== 'all') {
        filtered = filtered.filter(p => p.isOngoing === filters.isOngoing);
    }

    if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase().trim();
        if (search) {
            filtered = filtered.filter(p => projectMatchesSearch(p, search));
        }
    }

    return filtered;
};

// ============================================
// EXPORT HELPERS
// ============================================

/**
 * Format project for export (CSV/JSON)
 * @param project - Project to format
 * @returns Formatted project object
 */
export const formatProjectForExport = (project: Project) => {
    if (!project) return null;
    return {
        id: project._id,
        title: project.title,
        subtitle: project.subtitle || '',
        category: formatCategoryLabel(project.category),
        status: formatStatusLabel(project.status),
        impact: formatImpactLabel(project.impact),
        description: project.description,
        fullDescription: project.fullDescription || '',
        startDate: project.startDate ? formatDate(project.startDate) : '',
        endDate: project.endDate ? formatDate(project.endDate) : '',
        isOngoing: project.isOngoing ? 'Yes' : 'No',
        regions: (project.regions || []).join('; '),
        districts: (project.districts || []).join('; '),
        tags: (project.tags || []).join('; '),
        featured: project.featured ? 'Yes' : 'No',
        views: project.views || 0,
        createdAt: project.createdAt ? formatDate(project.createdAt) : '',
        updatedAt: project.updatedAt ? formatDate(project.updatedAt) : ''
    };
};